import React, { useEffect, useState, useCallback } from "react";
import { buildApiUrl } from "../utils/apiConfig";
import { isSignedInWithGoogle } from "../utils/auth";
import { useAuth } from "../contexts/AuthContext";

type PickerFile = {
    id: string;
    name: string;
    mimeType: string;
    sizeBytes: number;
    iconUrl: string;
    url: string;
};

interface GoogleDrivePickerProps {
    onFilesSelected?: (files: PickerFile[]) => void;
    onError?: (error: string) => void;
    disabled?: boolean;
}

declare global {
    interface Window {
        gapi: any;
        google: any;
    }
}

const GoogleDrivePicker: React.FC<GoogleDrivePickerProps> = ({
    onFilesSelected,
    onError,
    disabled = false,
}) => {
    const [pickerApiLoaded, setPickerApiLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { googleConnected } = useAuth();
    const signedInWithGoogle = googleConnected || isSignedInWithGoogle();

    useEffect(() => {
        // Load Google API script
        const loadGoogleApi = () => {
            if (window.gapi) {
                setPickerApiLoaded(true);
                return;
            }

            const script = document.createElement("script");
            script.src = "https://apis.google.com/js/api.js";
            script.async = true;
            script.defer = true;
            script.onload = () => {
                window.gapi.load("picker", () => {
                    setPickerApiLoaded(true);
                });
            };
            script.onerror = () => {
                if (onError) {
                    onError("Failed to load Google Picker API");
                }
            };
            document.body.appendChild(script);
        };

        loadGoogleApi();
    }, [onError]);

    const pickerCallback = useCallback(
        async (data: any) => {
            if (data.action === window.google.picker.Action.PICKED) {
                const selectedFiles: PickerFile[] = data.docs.map((doc: any) => ({
                    id: doc.id,
                    name: doc.name,
                    mimeType: doc.mimeType,
                    sizeBytes: doc.sizeBytes || 0,
                    iconUrl: doc.iconUrl || "",
                    url: doc.url || "",
                }));

                try {
                    // Send selected files to backend
                    const response = await fetch(buildApiUrl("api/user/drive/save-selected-files"), {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        credentials: "include",
                        body: JSON.stringify({ files: selectedFiles }),
                    });

                    if (!response.ok) {
                        throw new Error("Failed to save selected files");
                    }

                    if (onFilesSelected) {
                        onFilesSelected(selectedFiles);
                    }
                } catch (error: any) {
                    if (onError) {
                        onError(error.message || "Failed to save selected files");
                    }
                }
            } else if (data.action === window.google.picker.Action.CANCEL) {
                // User canceled the picker
            }
        },
        [onFilesSelected, onError]
    );

    const createPicker = useCallback(async () => {
        if (!pickerApiLoaded || !window.google || !window.google.picker) {
            if (onError) {
                onError("Google Picker API not loaded");
            }
            return;
        }

        if (!signedInWithGoogle) {
            if (onError) {
                onError("Please sign in with Google to access Drive files");
            }
            return;
        }

        setIsLoading(true);

        try {
            // Get OAuth access token from backend
            const tokenResponse = await fetch(buildApiUrl("api/google-drive/access-token"), {
                credentials: "include",
            });

            if (!tokenResponse.ok) {
                throw new Error("Failed to get access token");
            }

            const { accessToken } = await tokenResponse.json();

            if (!accessToken) {
                throw new Error("No access token available");
            }

            const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
                .setIncludeFolders(true);

            const imageView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS_IMAGES)
                .setIncludeFolders(true);

            const picker = new window.google.picker.PickerBuilder()
                .addView(view)
                .addView(imageView)
                .setOAuthToken(accessToken)
                .setCallback(pickerCallback)
                .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
                .build();

            picker.setVisible(true);
        } catch (error: any) {
            if (onError) {
                onError(error.message || "Failed to open Google Drive Picker");
            }
        } finally {
            setIsLoading(false);
        }
    }, [pickerApiLoaded, signedInWithGoogle, onError, pickerCallback]);


    const handleButtonClick = () => {
        if (!signedInWithGoogle) {
            if (onError) {
                onError("Please sign in with Google to access Drive files");
            }
            return;
        }
        createPicker();
    };

    return (
        <button
            onClick={handleButtonClick}
            disabled={disabled || isLoading || !pickerApiLoaded || !signedInWithGoogle}
            className="drive-picker-button"
        >
            {isLoading ? "Loading..." : "Select Files from Google Drive"}
        </button>
    );
};

export default GoogleDrivePicker;

